import { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import { ActionSelections } from '../../../core/types/chat.types'
import {
  YEAR_MAX,
  YEAR_MIN,
  isValidYear,
} from '../../../core/utils/explore-parameters'

type ExploreYearRangeFieldsProps = {
  initialYear1?: number
  initialYear2?: number
  onChange: (selections: Partial<ActionSelections>, isValid: boolean) => void
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--ds-fg-secondary, #535b6c)',
    margin: 0,
  },
  input: {
    width: '100%',
    padding: '10px 16px',
    borderRadius: 8,
    fontSize: '0.875rem',
    fontWeight: 500,
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    border: '1px solid var(--ds-border-default, #d7dae4)',
    color: 'var(--ds-fg-primary, #1f2430)',
    transition: 'all 200ms ease',
    outline: 'none',
    fontFamily: "'Poppins', sans-serif",
    '&:hover': { background: 'var(--ds-bg-subtle-hover, #e7eaf1)' },
    '&:focus': { borderColor: 'var(--color-coral-sender, #E8735A)', boxShadow: '0 0 0 2px rgba(232,115,90,0.12)' },
  },
}))

const ExploreYearRangeFields = ({
  initialYear1,
  initialYear2,
  onChange,
}: ExploreYearRangeFieldsProps) => {
  const classes = useStyles()

  const [year1, setYear1] = useState<number | ''>(
    isValidYear(initialYear1) ? (initialYear1 as number) : ''
  )
  const [year2, setYear2] = useState<number | ''>(
    isValidYear(initialYear2) ? (initialYear2 as number) : ''
  )

  useEffect(() => {
    const isValid =
      year1 !== '' && year2 !== '' &&
      year1 >= YEAR_MIN && year1 <= YEAR_MAX &&
      year2 >= YEAR_MIN && year2 <= YEAR_MAX

    const selections: Partial<ActionSelections> = {
      year1: year1 !== '' ? (year1 as number) : undefined,
      year2: year2 !== '' ? (year2 as number) : undefined,
    }

    onChange(selections, isValid)
  }, [year1, year2, onChange])

  return (
    <div className={classes.root}>
      <div className={classes.field}>
        <label className={classes.label}>Year 1</label>
        <input
          type="number"
          className={classes.input}
          value={year1}
          min={YEAR_MIN}
          max={YEAR_MAX}
          placeholder="YYYY"
          onChange={(e) => setYear1(e.target.value === '' ? '' : Number(e.target.value))}
        />
      </div>
      <div className={classes.field}>
        <label className={classes.label}>Year 2</label>
        <input
          type="number"
          className={classes.input}
          value={year2}
          min={YEAR_MIN}
          max={YEAR_MAX}
          placeholder="YYYY"
          onChange={(e) => setYear2(e.target.value === '' ? '' : Number(e.target.value))}
        />
      </div>
    </div>
  )
}

export default ExploreYearRangeFields
